// Session storage — the only code that touches the session's localStorage keys.
//
// The token is a JWT signed by the API with a secret the browser never has.
// The browser stores it and sends it; it cannot read anything useful out of it
// beyond its expiry, and it cannot make one. Who the user is — name, role,
// permissions, sedes — comes from the API's `meResolver` (see SessionProvider).
//
// This replaces tokens the browser decrypted with REACT_APP_TOKEN_SECRET and
// re-encrypted when permissions changed. That secret was compiled into the
// public bundle and was the same key the API trusted, so anyone could mint a
// session for any user.

const TOKEN_KEY = "token";
const HEADQUARTER_KEY = "selectedHeadquarterId";

// localStorage can throw (private mode, storage disabled) — a session that
// cannot be stored is simply no session.
const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null || value === undefined || value === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Nothing to do: the session just won't survive a reload.
  }
};

export const getToken = () => read(TOKEN_KEY);
export const setToken = (token) => write(TOKEN_KEY, token);

export const getSelectedHeadquarterId = () => read(HEADQUARTER_KEY);
export const setSelectedHeadquarterId = (id) => write(HEADQUARTER_KEY, id);

/** Forgets the session on this device. */
export const clearSession = () => {
  write(TOKEN_KEY, null);
  write(HEADQUARTER_KEY, null);
};

/**
 * The token's payload, decoded but NOT verified — only the API can verify a
 * token. Used for nothing but reading `exp`, so an expired session can be
 * dropped without a round trip. Never trust anything else in it.
 */
export const decodeTokenPayload = (token) => {
  try {
    const [, payload] = String(token).split(".");
    if (!payload) return null;

    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * Whether the stored token is missing, malformed, or past its expiry. An old
 * pre-JWT token reads as malformed, so it is discarded on first load.
 */
export const isTokenUsable = (token = getToken()) => {
  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return false;

  // A few seconds of slack for clock skew between browser and API.
  return payload.exp * 1000 > Date.now() + 5000;
};

/** "USER" (staff) or "STUDENT", or null. For routing before `me` returns. */
export const getTokenKind = (token = getToken()) =>
  decodeTokenPayload(token)?.kind ?? null;

// Why the last session ended, carried across the full page load that logout
// does, so the login page can say it. sessionStorage: this tab only, gone when
// it closes.
const LOGOUT_NOTICE_KEY = "logoutNotice";

export const setLogoutNotice = (text) => {
  try {
    if (text) sessionStorage.setItem(LOGOUT_NOTICE_KEY, text);
  } catch {
    // The notice is a courtesy; losing it is fine.
  }
};

/** Returns the notice once and forgets it. */
export const takeLogoutNotice = () => {
  try {
    const text = sessionStorage.getItem(LOGOUT_NOTICE_KEY);
    sessionStorage.removeItem(LOGOUT_NOTICE_KEY);
    return text;
  } catch {
    return null;
  }
};

// "The API says this session is over" — raised by the Apollo error link when a
// request carrying a token comes back UNAUTHENTICATED (expired, revoked by a
// password change, account disabled), heard by SessionProvider. A plain
// listener set because the link lives outside React.
const invalidListeners = new Set();

/** Subscribe; returns the unsubscribe function. */
export const onSessionInvalid = (listener) => {
  invalidListeners.add(listener);
  return () => invalidListeners.delete(listener);
};

/** `reason` is the API's extensions.reason: INVALID, REVOKED, ACCOUNT_GONE. */
export const notifySessionInvalid = (reason, message) => {
  invalidListeners.forEach((listener) => listener(reason, message));
};

/**
 * The sede to work in for a staff user: the stored choice if it is still one
 * of theirs, else their default, else their first.
 */
export const pickHeadquarterId = (headquarters = []) => {
  const stored = getSelectedHeadquarterId();
  if (stored && headquarters.some((hq) => hq.headquarterId === stored)) {
    return stored;
  }

  return (
    headquarters.find((hq) => hq.isDefault)?.headquarterId ||
    headquarters[0]?.headquarterId ||
    null
  );
};
